import React from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';

class SessionWarningDialog extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      countdown: props.secondsRemaining || 300,
    };
    this.countdownInterval = null;
  }

  componentDidMount() {
    if (this.props.open) {
      this.startCountdown();
    }
  }

  componentDidUpdate(prevProps) {
    const justOpened = this.props.open && !prevProps.open;
    const changedWhileOpen = this.props.open && (
      prevProps.warningType !== this.props.warningType ||
      prevProps.secondsRemaining !== this.props.secondsRemaining
    );
    if (justOpened || changedWhileOpen) {
      this.stopCountdown();
      this.setState({countdown: this.props.secondsRemaining || 300});
      this.startCountdown();
    } else if (!this.props.open && prevProps.open) {
      this.stopCountdown();
    }
  }

  componentWillUnmount() {
    this.stopCountdown();
  }

  startCountdown() {
    this.stopCountdown();
    this.countdownInterval = setInterval(() => {
      this.setState((prev) => {
        const next = Math.max(0, prev.countdown - 1);
        if (next === 0) {
          this.stopCountdown();
          if (this.props.open && this.props.onLogout) {
            this.props.onLogout();
          }
        }
        return {countdown: next};
      });
    }, 1000);
  }

  stopCountdown() {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
  }

  formatTime(seconds) {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  getDialogContent() {
    const {warningType} = this.props;
    const {countdown} = this.state;

    if (warningType === 'idle') {
      return {
        title: 'Session Expiring Due to Inactivity',
        message: `Your session will expire in ${this.formatTime(countdown)} due to inactivity. Click "Stay Logged In" to refresh your session and continue working.`,
        severity: 'warning',
        canExtend: true,
        showTip: true,
      };
    } else if (warningType === 'token-refresh-failed') {
      return {
        title: 'Session Could Not Be Refreshed',
        message: `Your session could not be automatically refreshed. Click "Stay Logged In" to reconnect, or you will be signed out in ${this.formatTime(countdown)}.`,
        severity: 'warning',
        canExtend: true,
        showTip: false,
      };
    } else if (warningType === 'sliding-window-warning') {
      return {
        title: 'Maximum Session Time Approaching',
        message: `For security, your session will expire in ${this.formatTime(countdown)} after 12 hours of use. Please save your work. You will need to log in again.`,
        severity: 'error',
        canExtend: false,
        showTip: false,
      };
    }

    // Default fallback
    return {
      title: 'Session Warning',
      message: 'Your session is about to expire.',
      severity: 'info',
      canExtend: true,
      showTip: false,
    };
  }

  render() {
    const {open, onExtend, onLogout, isExtending} = this.props;
    const content = this.getDialogContent();

    return (
      <Dialog
        open={open}
        onClose={(event, reason) => {
          // Prevent closing via backdrop click or escape key
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            return;
          }
        }}
        maxWidth="sm"
        fullWidth
        className="session-warning-dialog"
        data-testid="session-warning-dialog"
      >
        <DialogTitle>{content.title}</DialogTitle>
        <DialogContent>
          <div className="dialog-message">
            {content.message}
          </div>
          {!content.canExtend && (
            <div className="dialog-alert">
              <strong>Important:</strong> This is a security measure. Please save your work now.
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={onLogout}
            className="btn btn-default"
            disabled={isExtending}
            data-testid="session-warning-logout"
          >
            Logout Now
          </Button>
          {content.canExtend && (
            <Button
              onClick={onExtend}
              className="btn btn-action"
              variant="contained"
              disabled={isExtending}
              data-testid="session-warning-stay"
            >
              {isExtending ? (
                <>
                  <CircularProgress size={20} className="spinner" />
                  Refreshing...
                </>
              ) : (
                'Stay Logged In'
              )}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    );
  }
}

export default SessionWarningDialog;
